import { ArrowUpOnSquareIcon, DocumentDuplicateIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useSearchParams } from 'react-router';
import { CataloguePublishModal } from 'softcat:components/catalogue-publish-modal.tsx';
import { CatalogueStatus } from 'softcat:components/catalogue-status.tsx';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { ConfirmationModal } from 'softcat:components/confirmation-modal.tsx';
import { DataTable } from 'softcat:components/data-table.tsx';
import { SearchParametersOrderBy } from 'softcat:components/search-parameters-order-by.tsx';
import { SearchParametersPagination } from 'softcat:components/search-parameters-pagination.tsx';
import { SearchParametersSearchField } from 'softcat:components/search-parameters-search-field.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { usePaginationState } from 'softcat:hooks/use-pagination-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { CatalogueService } from 'softcat:services/catalogue.ts';
import { SoftcatApiCatalogue } from 'softcat:types/softcat-api.ts';
import useSWR, { preload } from 'swr';

export const Catalogues = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [searchParameters, setSearchParameters] = useSearchParams();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmClone, setShowConfirmClone] = useState(false);
    const [showPublishCatalogue, setShowPublishCatalogue] = useState(false);
    const [selectedCatalogue, setSelectedCatalogue] = useState({ name: '', version: '' });
    const [onRemoveErrorMessage, setOnRemoveErrorMessage] = useState('');
    const [onCloneErrorMessage, setOnCloneErrorMessage] = useState('');
    const limit = Number.parseInt(searchParameters.get('limit') || '10');
    const page = Number.parseInt(searchParameters.get('page') || '1');
    const query = searchParameters.get('query') || '';
    const orderBy = searchParameters.get('orderBy') || '';

    const pagination = usePaginationState(JSON.stringify([activeTerritory, limit, orderBy, query]));

    const firstPageFallbacks = useRef({ count: 0, pending: false });

    if (firstPageFallbacks.current.pending && page === 1) {
        firstPageFallbacks.current = { count: firstPageFallbacks.current.count + 1, pending: false };
    }

    const firstPageSeed = useRef<{ catalogues: SoftcatApiCatalogue[]; sequence: number }>();

    const showFirstPage = () => {
        setSearchParameters(
            (current) => {
                const next = new URLSearchParams(current);

                next.set('page', '1');

                return next;
            },
            { replace: true },
        );
    };

    const { data, error, isLoading, mutate } = useSWR(
        {
            fallbacks: firstPageFallbacks.current.count,
            key: `${activeTerritory}-CatalogueServiceFindAll`,
            limit,
            orderBy,
            page: page - 1,
            query,
            sequence: pagination.sequenceId,
        },
        async ({ limit, orderBy, page, query, sequence }: { limit: number; orderBy: string; page: number; query: string; sequence: number }) => {
            const mode = pagination.mode;
            const tokenForPage = pagination.tokenForPage(page);
            const fetchPage = async (pageToFetch: number, requestedToken?: string) => {
                if (pageToFetch === 0 && firstPageSeed.current?.sequence === sequence) {
                    const { catalogues } = firstPageSeed.current;

                    firstPageSeed.current = undefined;

                    return { catalogues };
                }

                const { nextToken, catalogues } = await CatalogueService.findAllWithNextToken({
                    limit,
                    nextToken: requestedToken,
                    orderBy,
                    page: pageToFetch,
                    query,
                });

                pagination.recordPage(sequence, pageToFetch, nextToken);

                return { catalogues, nextToken };
            };

            const detectedFirstPage = page > 0 && mode === 'unknown' ? await fetchPage(0) : undefined;

            if (!pagination.isCurrent(sequence)) {
                return detectedFirstPage?.catalogues ?? [];
            }

            const isTokenMode = mode === 'token' || (mode === 'unknown' && detectedFirstPage?.nextToken !== undefined);

            if (page > 0 && isTokenMode && (detectedFirstPage || !tokenForPage)) {
                const firstPage = detectedFirstPage ?? (await fetchPage(0));

                firstPageSeed.current = { catalogues: firstPage.catalogues, sequence };
                firstPageFallbacks.current.pending = true;
                showFirstPage();

                return firstPage.catalogues;
            }

            const currentPage = await fetchPage(page, tokenForPage);

            return currentPage.catalogues;
        },
    );

    const columns = useMemo(() => {
        return [
            { key: 'name', value: intl.formatMessage({ id: 'CATALOGUES_PAGE_COLUMN_NAME' }), visible: true },
            { key: 'version', value: intl.formatMessage({ id: 'CATALOGUES_PAGE_COLUMN_VERSION' }), visible: true },
            { key: 'status', value: intl.formatMessage({ id: 'CATALOGUES_PAGE_COLUMN_STATUS' }), visible: true },
            { key: 'catalogueExpiry', value: intl.formatMessage({ id: 'CATALOGUES_PAGE_COLUMN_CATALOGUE_EXPIRY' }), visible: true },
            { key: 'actions', value: intl.formatMessage({ id: 'CATALOGUES_PAGE_COLUMN_ACTIONS' }), visible: false },
        ];
    }, [intl]);

    const removeCatalogue = async () => {
        try {
            await CatalogueService.remove({ name: selectedCatalogue.name, version: selectedCatalogue.version });

            await mutate();

            setShowConfirmDelete(false);
        } catch (error) {
            setOnRemoveErrorMessage(`${intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_DELETE_ERROR' })}: ${(error as Error).message}`);
        }
    };

    const cloneCatalogue = async () => {
        try {
            const catalogue = await CatalogueService.findOne({ name: selectedCatalogue.name, version: selectedCatalogue.version });

            await CatalogueService.create({
                configVersion: catalogue.configVersion,
                name: `${catalogue.name} (clone)`,
                packages: catalogue.packages,
                status: catalogue.status,
                version: catalogue.version,
            });

            await mutate();

            setShowConfirmClone(false);
        } catch (error) {
            setOnCloneErrorMessage(`${intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_CLONE_ERROR' })}: ${(error as Error).message}`);
        }
    };

    const onSuccessfulPublish = async () => {
        setShowPublishCatalogue(false);

        await mutate();
    };

    const rows = useMemo(() => {
        return (
            data?.map((catalogue, index) => {
                return {
                    actions: (
                        <div className={'flex gap-4 justify-self-end'}>
                            <button
                                data-testid={`publish-catalogue-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_PUBLISH' })}
                                data-tooltip-id={'app-tooltip'}
                                onClick={() => {
                                    setShowPublishCatalogue(true);
                                    setSelectedCatalogue({ name: catalogue.name, version: catalogue.version });
                                }}
                                onMouseEnter={() => {
                                    return preload(
                                        { key: `${activeTerritory}-CatalogueServiceFindOne`, name: catalogue.name, version: catalogue.version },
                                        CatalogueService.findOne,
                                    );
                                }}
                            >
                                <ArrowUpOnSquareIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </button>
                            <button
                                data-testid={`clone-catalogue-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_CLONE' })}
                                data-tooltip-id={'app-tooltip'}
                                onClick={() => {
                                    setSelectedCatalogue({ name: catalogue.name, version: catalogue.version });
                                    setOnCloneErrorMessage('');
                                    setShowConfirmClone(true);
                                }}
                            >
                                <DocumentDuplicateIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </button>
                            <NavLink
                                data-testid={`edit-catalogue-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_EDIT' })}
                                data-tooltip-id={'app-tooltip'}
                                onMouseEnter={() => {
                                    return preload(
                                        { key: `${activeTerritory}-CatalogueServiceFindOne`, name: catalogue.name, version: catalogue.version },
                                        CatalogueService.findOne,
                                    );
                                }}
                                to={`edit/${catalogue.name}/${catalogue.version}`}
                                type="button"
                            >
                                <PencilIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </NavLink>
                            <button
                                data-testid={`delete-catalogue-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_DELETE' })}
                                data-tooltip-id={'app-tooltip'}
                                onClick={() => {
                                    setSelectedCatalogue({ name: catalogue.name, version: catalogue.version });
                                    setOnRemoveErrorMessage('');
                                    setShowConfirmDelete(true);
                                }}
                            >
                                <TrashIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </button>
                        </div>
                    ),
                    catalogueExpiry: <CertificateStatus certificateExpiry={catalogue.notAfter} />,
                    name: catalogue.name,
                    status: <CatalogueStatus status={catalogue.status} />,
                    version: catalogue.version,
                };
            }) || []
        );
    }, [activeTerritory, data, intl]);

    return (
        <div className="space-y-6 px-4">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">
                        <FormattedMessage id={'CATALOGUES_PAGE_HEADING'} />
                    </h1>

                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'CATALOGUES_PAGE_DESCRIPTION'} />
                    </p>
                </div>

                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <NavLink
                        className="flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        to={'create'}
                        type="button"
                    >
                        <PlusIcon className={'size-3'} />
                        <FormattedMessage id={'CATALOGUES_PAGE_ADD_BUTTON'} />
                    </NavLink>
                </div>
            </div>

            <div className={'flex items-center'}>
                <div className={'grow'}>
                    <SearchParametersSearchField placeholder={intl.formatMessage({ id: 'CATALOGUES_PAGE_SEARCH_PLACEHOLDER' })} />
                </div>

                <div className={'pl-4'}>
                    <SearchParametersOrderBy
                        options={[
                            { label: intl.formatMessage({ id: 'CATALOGUES_PAGE_ORDER_BY_NONE' }), value: '' },
                            { label: intl.formatMessage({ id: 'CATALOGUES_PAGE_ORDER_BY_NAME_DESC' }), value: 'name:desc' },
                            { label: intl.formatMessage({ id: 'CATALOGUES_PAGE_ORDER_BY_NAME_ASC' }), value: 'name:asc' },
                            { label: intl.formatMessage({ id: 'CATALOGUES_PAGE_ORDER_BY_VERSION_DESC' }), value: 'version:desc' },
                            { label: intl.formatMessage({ id: 'CATALOGUES_PAGE_ORDER_BY_VERSION_ASC' }), value: 'version:asc' },
                        ]}
                    />
                </div>
            </div>

            {error && (
                <p className="mt-2 text-sm text-gray-700">
                    <FormattedMessage id={'CATALOGUES_PAGE_ERROR_LOADING_DATA'} />
                </p>
            )}

            {isLoading && <Spinner />}

            {!error && !isLoading && (
                <div>
                    {rows.length > 0 ? (
                        <>
                            <div className={'overflow-auto'}>
                                <DataTable columns={columns} rows={rows} />
                            </div>

                            <div className={'border-t border-t-gray-200 pt-2'}>
                                <SearchParametersPagination
                                    hasNext={pagination.mode === 'token' ? !!pagination.tokenForPage(page) : undefined}
                                    total={rows.length}
                                />
                            </div>
                        </>
                    ) : (
                        <p className="mt-2 text-sm text-gray-700">
                            <FormattedMessage id={'CATALOGUES_PAGE_NO_RESULTS'} />
                        </p>
                    )}
                </div>
            )}

            <ConfirmationModal
                confirmButtonText={intl.formatMessage({ id: 'BUTTON_DELETE' })}
                confirmErrorMessage={onRemoveErrorMessage}
                content={intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_DELETE_DESCRIPTION' }, { catalogue: selectedCatalogue.name })}
                handleClose={() => {
                    setShowConfirmDelete(false);
                }}
                handleConfirm={async () => {
                    await removeCatalogue();
                }}
                header={intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_DELETE_TITLE' })}
                isVisible={showConfirmDelete}
            />

            <ConfirmationModal
                confirmButtonText={intl.formatMessage({ id: 'BUTTON_CLONE' })}
                confirmErrorMessage={onCloneErrorMessage}
                content={intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_CLONE_DESCRIPTION' }, { catalogue: selectedCatalogue.name })}
                handleClose={() => {
                    setShowConfirmClone(false);
                }}
                handleConfirm={async () => {
                    await cloneCatalogue();
                }}
                header={intl.formatMessage({ id: 'CATALOGUES_PAGE_CONFIRM_CLONE_TITLE' })}
                isVisible={showConfirmClone}
                style={'submit'}
            />

            {selectedCatalogue.name && selectedCatalogue.version && (
                <CataloguePublishModal
                    handleClose={onSuccessfulPublish}
                    isVisible={showPublishCatalogue}
                    name={selectedCatalogue.name}
                    version={selectedCatalogue.version}
                />
            )}
        </div>
    );
};
